import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, devWarn } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { KeyRound, Lock, Plus, Save, Trash2, Unlock, X } from "lucide-react";
import { toast } from "sonner";
import { PermissionsEditor } from "../components/PermissionsEditor";
import { RecentLocks } from "../components/RecentLocks";
import { EMPTY_PERMS, MODULES } from "../lib/perms";

const permsSummary = (p) =>
  MODULES.filter((m) => p?.[m.key] && p[m.key] !== "none")
    .map((m) => (m.key === "invoices" ? `Facturi (${p.invoices === "all" ? "toate" : "ale lui"})` : m.label))
    .join(", ") || "Fara acces";

export default function AccountsPage() {
  const [users, setUsers] = useState([]);
  const [locks, setLocks] = useState({});
  const [recentLocks, setRecentLocks] = useState([]);
  const [unlockingEmail, setUnlockingEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, email, name, password }
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", permissions: EMPTY_PERMS });

  const load = async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([api.get("/admin/users"), api.get("/admin/locks")]);
      setUsers(r.data);
      setRecentLocks(l.data);
      setLocks(Object.fromEntries(l.data.filter((x) => x.active).map((x) => [x.email, x.locked_until])));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Refresh lockouts every 15s so new ones show up without reloading.
  useEffect(() => {
    const id = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get("/admin/locks");
        setRecentLocks(data);
        setLocks(Object.fromEntries(data.filter((x) => x.active).map((x) => [x.email, x.locked_until])));
      } catch (e) {
        devWarn("locks refresh failed", e);
      }
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const handleCreate = async () => {
    if (!form.email.trim() || form.password.length < 6) {
      toast.error("Email valid + parola minim 6 caractere");
      return;
    }
    try {
      await api.post("/admin/users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        permissions: form.permissions,
      });
      toast.success("Cont creat");
      setForm({ email: "", password: "", name: "", permissions: EMPTY_PERMS });
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    const payload = {};
    if (editing.email && editing.email !== editing._origEmail) payload.email = editing.email;
    if (editing.name !== editing._origName) payload.name = editing.name;
    if (editing.password) payload.password = editing.password;
    if (JSON.stringify(editing.permissions) !== JSON.stringify(editing._origPerms))
      payload.permissions = editing.permissions;
    if (Object.keys(payload).length === 0) {
      setEditing(null);
      return;
    }
    try {
      await api.put(`/admin/users/${editing.id}`, payload);
      toast.success("Cont actualizat");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  const handleUnlock = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unlock`);
      toast.success(`Cont deblocat: ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  const handleUnlockEmail = async (email) => {
    setUnlockingEmail(email);
    try {
      await api.post("/admin/locks/unlock", { email });
      toast.success(`Deblocat: ${email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setUnlockingEmail(null);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Sterge contul ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Cont sters");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="accounts-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Administrare
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
            Conturi
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Creaza, modifica si sterge conturi suplimentare si alege la ce sectiuni are acces fiecare
            cont. Adminul are acces la tot.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="rounded-sm bg-black hover:bg-zinc-800 text-white h-10"
          data-testid="new-account-button"
        >
          <Plus size={14} className="mr-2" /> Cont nou
        </Button>
      </div>

      <RecentLocks locks={recentLocks} onUnlock={handleUnlockEmail} busyEmail={unlockingEmail} />

      {creating && (
        <div className="border border-zinc-300 rounded-sm bg-zinc-50 p-5 mb-6" data-testid="new-account-form">
          <h2 className="font-display text-xl font-bold tracking-tight mb-3">Cont nou</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Email</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@costanov.ro"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="new-account-email"
              />
            </div>
            <div>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Nume (optional)</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Popescu"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="new-account-name"
              />
            </div>
            <div>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Parola</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="min. 6 caractere"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="new-account-password"
              />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">Acces permis</Label>
            <div className="mt-2">
              <PermissionsEditor
                value={form.permissions}
                onChange={(permissions) => setForm({ ...form, permissions })}
                testPrefix="new-account"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleCreate} className="rounded-sm bg-black text-white" data-testid="save-new-account">
              <Save size={14} className="mr-2" /> Creeaza
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                setForm({ email: "", password: "", name: "", permissions: EMPTY_PERMS });
              }}
              className="rounded-sm border-zinc-300"
              data-testid="cancel-new-account"
            >
              <X size={14} className="mr-2" /> Anuleaza
            </Button>
          </div>
        </div>
      )}

      <div className="border border-zinc-200 rounded-sm bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Email
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Nume
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide w-24">
                Rol
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right w-44">
                Actiuni
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-zinc-500 py-6">
                  Se incarca...
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-zinc-500 py-6">
                  Niciun cont
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => {
                const isEditing = editing?.id === u.id;
                return (
                  <React.Fragment key={u.id}>
                  <TableRow data-testid={`account-row-${u.id}`}>
                    <TableCell className="text-sm">
                      {isEditing ? (
                        <Input
                          value={editing.email}
                          onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                          className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
                          data-testid={`edit-email-${u.id}`}
                        />
                      ) : (
                        <span className="font-mono">{u.email}</span>
                      )}
                      {!isEditing && locks[u.email] && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800"
                          data-testid={`account-locked-badge-${u.id}`}
                          title={`Blocat pana la ${new Date(locks[u.email]).toLocaleTimeString("ro-RO")}`}
                        >
                          <Lock size={10} /> Blocat pana la {new Date(locks[u.email]).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {isEditing ? (
                        <Input
                          value={editing.name}
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
                          data-testid={`edit-name-${u.id}`}
                        />
                      ) : (
                        u.name || "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                          u.role === "admin"
                            ? "bg-black text-white"
                            : "bg-zinc-100 text-zinc-700"
                        }`}
                      >
                        {u.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-1 items-center">
                          <Input
                            type="password"
                            value={editing.password}
                            onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                            placeholder="parola noua (optional)"
                            className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-xs w-44"
                            data-testid={`edit-password-${u.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={handleSave}
                            className="rounded-sm bg-black text-white h-8 px-2"
                            data-testid={`save-account-${u.id}`}
                          >
                            <Save size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditing(null)}
                            className="rounded-sm border-zinc-300 h-8 px-2"
                            data-testid={`cancel-account-${u.id}`}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          {locks[u.email] && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnlock(u)}
                              className="rounded-sm border-amber-300 h-8 px-2 text-amber-800 hover:bg-amber-50"
                              data-testid={`unlock-account-${u.id}`}
                            >
                              <Unlock size={12} className="mr-1" /> Deblocheaza
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setEditing({
                                id: u.id,
                                email: u.email,
                                name: u.name || "",
                                password: "",
                                _origEmail: u.email,
                                _origName: u.name || "",
                                permissions: u.permissions || EMPTY_PERMS,
                                _origPerms: u.permissions || EMPTY_PERMS,
                              })
                            }
                            className="rounded-sm border-zinc-300 h-8 px-2"
                            data-testid={`edit-account-${u.id}`}
                          >
                            <KeyRound size={12} className="mr-1" /> Modifica
                          </Button>
                          {u.role !== "admin" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(u)}
                              className="rounded-sm border-zinc-300 h-8 px-2 text-red-600 hover:bg-red-50"
                              data-testid={`delete-account-${u.id}`}
                            >
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  {u.role !== "admin" && (
                    <TableRow className="hover:bg-transparent" data-testid={`account-perms-row-${u.id}`}>
                      <TableCell colSpan={4} className="pt-0 pb-3">
                        {isEditing ? (
                          <PermissionsEditor
                            value={editing.permissions}
                            onChange={(permissions) => setEditing({ ...editing, permissions })}
                            testPrefix={`edit-${u.id}`}
                          />
                        ) : (
                          <span className="text-xs text-zinc-500 font-mono" data-testid={`account-perms-summary-${u.id}`}>
                            Acces: {permsSummary(u.permissions)}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
